import React, { useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  addActivity,
  getActivityDetails,
  updateActivity,
} from "../../data/redux/activity/_activityAction";
import * as Yup from "yup";
import { useFormik } from "formik";

interface ActivityModalProps {
  show: boolean;
  onHide: () => void;
  uuid: string | null;
  onSuccess?: () => void;
}

const ActivityModal: React.FC<ActivityModalProps> = ({
  show,
  onHide,
  uuid,
  onSuccess,
}) => {
  const dispatch: any = useDispatch();

  // Formik setup with validation schema
  const formik = useFormik({
    initialValues: {
      activityName: "",
      activityType: "",
      participationLevel: "",
      taxCategory: "",
      description: "",
    },
    validationSchema: Yup.object({
      activityName: Yup.string()
        .trim()
        .max(100, "Activity Name cannot exceed 100 characters")
        .required("Activity Name is required"),
      activityType: Yup.string().required("Activity Type is required"),
      participationLevel: Yup.string().required(
        "Participation Level is required"
      ),
      taxCategory: Yup.string()
        .trim()
        .max(100, "Tax Category cannot exceed 100 characters")
        .required("Tax Category is required"),
      description: Yup.string()
        .trim()
        .max(500, "Description cannot exceed 500 characters")
        .required("Description is required"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,

    onSubmit: async (values) => {
      const activityData = {
        name: values.activityName,
        type: values.activityType,
        participation_level: values.participationLevel,
        tax_category: values.taxCategory,
        description: values.description,
      };

      if (uuid) {
        await dispatch(updateActivity({ uuid, values: activityData }));
      } else {
        await dispatch(addActivity(activityData));
      }
      onHide();
    },
  });

  // Fetch activity details when modal is shown and uuid is provided
  useEffect(() => {
    if (show && uuid) {
      dispatch(getActivityDetails({ uuid })).then((response: any) => {
        const activity = response?.payload?.data;
        formik.setValues({
          activityName: activity?.name || "",
          activityType: activity?.type || "",
          participationLevel: activity?.participation_level || "",
          taxCategory: activity?.tax_category || "",
          description: activity?.description || "",
        });
      });
    } else {
      formik.resetForm();
    }
  }, [show, uuid, dispatch]);

  return (
    <Modal
      className="nonrealactivitiesmodal"
      show={show}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {uuid ? "Edit Activity" : "Create New Activity"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={formik.handleSubmit}>
          {/* Activity Name */}
          <div className="mb-3">
            <label className="form-label">Activity Name</label>
            <input
              type="text"
              placeholder="Enter Activity Name"
              className="commonInput bg-light form-control"
              name="activityName"
              value={formik.values.activityName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.activityName && formik.errors.activityName && (
              <div className="text-danger">{formik.errors.activityName}</div>
            )}
          </div>

          {/* Activity Type */}
          <div className="mb-3">
            <label className="form-label">Activity Type</label>
            <select
              name="activityType"
              value={formik.values.activityType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="commonInput bg-light form-control"
            >
              <option value="" disabled>
                Activity Type
              </option>
              <option value="business">Business</option>
              <option value="investment">Investment</option>
              <option value="trading">Trading</option>
              <option value="other">Other</option>
            </select>
            {formik.touched.activityType && formik.errors.activityType && (
              <div className="text-danger">{formik.errors.activityType}</div>
            )}
          </div>

          {/* Participation Level */}
          <div className="mb-3">
            <div className="user-type-box mb-4  rounded ">
              <label className="form-label">Participation Level</label>
              <div className="btn-radio d-flex">
                <div className="me-3">
                  <input
                    type="radio"
                    id="active"
                    name="participationLevel"
                    value="active"
                    checked={
                      formik?.values?.participationLevel?.toLowerCase() ===
                      "active"
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <label htmlFor="spouse" className="radio-label">
                    Active
                  </label>
                </div>

                <div className="me-3">
                  <input
                    type="radio"
                    id="passive"
                    name="participationLevel"
                    value="passive"
                    checked={
                      formik?.values?.participationLevel?.toLowerCase() ===
                      "passive"
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <label htmlFor="taxPro" className="radio-label">
                    Passive
                  </label>
                </div>
              </div>
            </div>
            {formik.touched.participationLevel &&
              formik.errors.participationLevel && (
                <div className="invalid-feedback d-block">
                  {formik.errors.participationLevel}
                </div>
              )}
          </div>

          {/* Tax Category */}
          <div className="mb-3">
            <label className="form-label">Tax Category</label>
            <input
              type="text"
              placeholder="Enter Tax Category"
              className="commonInput bg-light form-control"
              name="taxCategory"
              value={formik.values.taxCategory}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.taxCategory && formik.errors.taxCategory && (
              <div className="text-danger">{formik.errors.taxCategory}</div>
            )}
          </div>

          {/* Description */}
          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              id="description"
              name="description"
              maxlength="500"
              rows={3}
              className="form-control commonInput bg-light"
              placeholder="Describe your activity in detail..."
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <div className="text-end small text-muted">
              {formik.values.description.length}/500
            </div>

            {formik.touched.description && formik.errors.description && (
              <div className="text-danger">{formik.errors.description}</div>
            )}
          </div>

          <Button
            className="log-time-button btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
            style={{
              backgroundColor: "#379fff",
              borderColor: "#379fff",
              width: "100%",
              fontWeight: 600,
            }}
            type="submit"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {uuid ? "Update Activity" : "Save Activity"}
          </Button>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ActivityModal;
